import { useTimeZone } from '@/auth/useTimeZone';
import { CoachViewableFeedbacksTable } from '@/components/feedbacks/coach-viewable-feedbacks-table';
import { InfoPage } from '@/components/info-page';
import { TeacherSessionDateDivider } from '@/components/session/teacher-session-date-divider';
import { EntityTableGroupBy } from '@/components/table/entity-table';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { EmptyArray, getCoachViewableSessionFeedbacks } from '@/lib/api-calls';
import { usePaginatedFilters } from '@/lib/filtering';
import { paginationRequestSchema } from '@/lib/pagination';
import { validateRouteRole } from '@/lib/routing';
import { groupByStartDate, StartDateGroupKey } from '@/lib/table-grouping-utils';
import { isCoach, isNotNullish } from '@airreading/common';
import {
  CoachViewableSessionFeedback,
  FeedbackSubmittedStatus,
  feedbackSubmittedStatusSchema,
  GetCoachViewableSessionFeedbacksModifier,
} from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/_layout/teachers-feedback/')({
  beforeLoad: validateRouteRole(isCoach),
  component: Feedbacks,
  validateSearch: paginationRequestSchema.extend({
    feedbackSubmitted: feedbackSubmittedStatusSchema.optional(),
    query: z.string().optional(),
  }),
});

function Feedbacks() {
  const { timeZone } = useTimeZone();

  const sessionGroupBy = useMemo<EntityTableGroupBy<CoachViewableSessionFeedback, StartDateGroupKey>>(
    () => ({
      getGroupKey: (row) => groupByStartDate(row.session.startInstant, timeZone),
      renderDivider: (groupKey, rowCount) => <TeacherSessionDateDivider groupKey={groupKey} rowCount={rowCount} />,
    }),
    [timeZone],
  );

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      feedbackSubmitted: [[feedbackSubmitted, activeFeedbackSubmitted], setFeedbackSubmitted],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'feedbackSubmitted']);

  const requestQuery = {
    pageIndex,
    pageSize,
    filters: {
      query: activeQuery,
      feedbackSubmitted: activeFeedbackSubmitted as FeedbackSubmittedStatus,
    },
  } satisfies GetCoachViewableSessionFeedbacksModifier;

  const {
    data: sessionFeedbacksResp,
    isPending: isLoadingSessionFeedbacks,
    error,
  } = useQuery(getCoachViewableSessionFeedbacks(requestQuery));
  const sessionFeedbacks = useMemo(() => sessionFeedbacksResp?.sessionFeedbacks || EmptyArray, [sessionFeedbacksResp]);

  const filterLeftComponents = useMemo(
    () => (
      <div className="flex w-full min-w-0 flex-col gap-2 md:flex-row md:items-center md:gap-0 md:space-x-2">
        <TableFilterInputField
          className="max-md:w-full max-md:max-w-none"
          data-testid="teachers-feedback-filter-input-search"
          placeholder="Search by student first or last name"
          value={query ?? ''}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />

        <div className="flex w-full min-w-0 flex-wrap items-center gap-2">
          <TableFilterDropdown
            data-testid="teachers-feedback-filter-dropdown-status"
            title="Feedback status"
            singleSelect
            options={[
              { value: FeedbackSubmittedStatus.not_submitted, label: 'Not submitted' },
              { value: FeedbackSubmittedStatus.sent, label: '✅ Sent' },
              { value: FeedbackSubmittedStatus.sent_over_24h, label: '✅ Sent (late)' },
            ]}
            selectedValues={[feedbackSubmitted].filter(isNotNullish)}
            onChange={(selected) => {
              setFeedbackSubmitted(selected.filter((v) => v !== feedbackSubmitted)[0]);
            }}
          />
          <TableFilterResetButton
            data-testid="teachers-feedback-filter-button-reset"
            show={true}
            onClick={resetFilters}
          />
        </div>
      </div>
    ),
    [feedbackSubmitted, query, resetFilters, setFeedbackSubmitted, setQuery],
  );

  if (error) {
    console.error(error);
    return (
      <InfoPage headerText="Class feedback">
        <div>We are sorry, but we could not fetch your class feedback. Please notify Air Reading about this issue.</div>
      </InfoPage>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col max-md:-mx-3 max-md:px-3">
      <InfoPage headerText="Class feedback" data-testid="teachers-feedback-page">
        <CoachViewableFeedbacksTable
          sessionFeedbacks={sessionFeedbacks}
          isLoading={isLoadingSessionFeedbacks}
          filters={{ filterLeftComponents }}
          filterBarClassName="max-md:flex-col max-md:items-stretch max-md:gap-2"
          filterLeftClassName="max-md:w-full max-md:items-stretch max-md:space-x-0"
          tableHookProps={{
            state: {
              pagination: {
                pageIndex,
                pageSize,
              },
            },
            onPaginationChange,
            rowCount: sessionFeedbacksResp?.total || 0,
          }}
          showPagination={true}
          groupBy={sessionGroupBy}
        />
      </InfoPage>
    </div>
  );
}
